import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, isValidElement, cloneElement, createContext, useContext } from 'react';
import styled from 'styled-components';
import { Box } from '../Box/index.js';
import { getItemStyles } from './styles.js';

/* ======================================= */
const ProgressTrackerContext = /*#__PURE__*/createContext({});
const useProgressTrackerContext = () => useContext(ProgressTrackerContext);
/* ======================================= */
const StyledProgressTracker = styled(Box).withConfig({
  displayName: "ProgressTracker__StyledProgressTracker",
  componentId: "sc-15lc846-0"
})(["> * + *{margin-left:", ";}"], _ref => {
  let {
    spacing
  } = _ref;
  return `${spacing}px` || null;
});
/* ======================================= */
/**
 * ProgressTracker is used to display the user's progress toward completing a set of tasks.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/progress-tracker#usage)
 * - [Props](https://blockparty.endpoint.com/components/progress-tracker#props)
 * <br />
 *
 */
const ProgressTracker = /*#__PURE__*/forwardRef((_ref2, forwardedRef) => {
  let {
    activeStep = 0,
    isInteractive = false,
    spacing = 1,
    stepHeight = 4,
    stepWidth,
    children,
    dataTestId = 'progress-tracker',
    ...props
  } = _ref2;
  const value = {
    stepWidth,
    stepHeight
  };
  const clonedChildren = React__default.Children.map(children, (child, index) => {
    if (! /*#__PURE__*/isValidElement(child)) return;
    if (child.type === ProgressTrackerItem) {
      return /*#__PURE__*/cloneElement(child, {
        activeStep: index + 1 === activeStep ? 1 + activeStep : null
      });
    }
    return child;
  });
  return /*#__PURE__*/React__default.createElement(ProgressTrackerContext.Provider, {
    value: value
  }, /*#__PURE__*/React__default.createElement(Box, {
    "aria-label": "progress",
    as: isInteractive ? 'nav' : undefined,
    role: !isInteractive ? 'group' : undefined
  }, /*#__PURE__*/React__default.createElement(StyledProgressTracker, _extends({
    ref: forwardedRef,
    alignItems: "center",
    as: "ol",
    "data-test-id": dataTestId,
    display: "flex",
    flexDirection: "row",
    spacing: spacing,
    width: "100%"
  }, props), clonedChildren)));
});
/**
 * ProgressTrackerItem is a single item used in ProgressTracker.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/progress-tracker#usage)
 * - [Props](https://blockparty.endpoint.com/components/progress-tracker#props)
 * <br />
 *
 */
const ProgressTrackerItem = /*#__PURE__*/forwardRef((_ref3, forwardedRef) => {
  let {
    activeStep,
    dataTestId = 'progress-tracker-item',
    status = 'upcoming',
    ...props
  } = _ref3;
  const {
    stepHeight,
    stepWidth
  } = useProgressTrackerContext();
  const itemStyles = getItemStyles(status);
  const ariaCurrent = status === 'current' && 'step';
  // @ts-ignore - default value from ProgressTracker wrapper is 4
  const activeStepHeight = activeStep ? stepHeight * 2 : stepHeight;
  return (
    /*#__PURE__*/
    // @ts-ignore
    React__default.createElement(Box, _extends({
      ref: forwardedRef,
      "aria-current": ariaCurrent,
      as: "li",
      "data-test-id": dataTestId,
      height: activeStepHeight,
      listStyleType: "none",
      maxWidth: stepWidth || '100%',
      width: "100%"
    }, itemStyles, props))
  );
});
ProgressTracker.displayName = 'ProgressTracker';
ProgressTrackerItem.displayName = 'ProgressTrackerItem';

export { ProgressTracker, ProgressTrackerItem };
